// КОПИЯ `services/face-landmarker-loader.ts` библиотеки biometric-liveness
// (Adilet → sc-frontend-atyrau `src/libs/biometric-liveness`), без правок. Этот
// файл от Vue не зависит, поэтому переносится как есть; правится там.
//
// Shared MediaPipe FaceLandmarker loader.
//
// Building the FaceLandmarker downloads the WASM runtime + the ~3.7MB model and
// initialises a GPU context — the slow part of opening Face ID. We create it
// ONCE and reuse it across sessions, and warm it up in the background at app
// start (preloadFaceLandmarker) so the face step opens instantly the first time.
//
// The heavy fetch/SIMD logic lives here; the BiometricLiveness component just
// calls getFaceLandmarker() and (optionally) forwards download progress to its UI.
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision'
/*
 * Рантайм и модель — через `?url`, а не из `/public`. Так Vite кладёт их в
 * `/assets/` с хэшем содержимого в имени, а nginx отдаёт `/assets/` с
 * `immutable` на год: после первой загрузки устройство больше не спрашивает
 * сервер о 3,6 МБ модели и 11 МБ рантайма, в том числе после выката. Из
 * `/public` они шли без Cache-Control, а ETag — от времени сборки образа, и
 * каждый выкат заставлял качать их заново, хотя байты не менялись. Обновится
 * пакет — сменится хэш, и старый рантайм из кэша не подтянется к новому JS.
 */
import simdLoaderUrl from '@mediapipe/tasks-vision/vision_wasm_internal.js?url'
import simdBinaryUrl from '@mediapipe/tasks-vision/vision_wasm_internal.wasm?url'
import noSimdLoaderUrl from '@mediapipe/tasks-vision/vision_wasm_nosimd_internal.js?url'
import noSimdBinaryUrl from '@mediapipe/tasks-vision/vision_wasm_nosimd_internal.wasm?url'
import bundledModelUrl from './face_landmarker.task?url'

// How many faces MediaPipe may return per frame. This is an upper bound (there
// is no "unlimited") — higher = more inference cost per frame. We detect several
// so the app can see how many people are in frame and pick the most central one
// as the primary subject / reject frames with more than one face in the oval.
// Raising/lowering this is the single knob for that budget.
const MAX_DETECTED_FACES = 5

// ── Configurable asset paths ────────────────────────────────────────────────
export interface FaceLandmarkerAssetConfig {
  /**
   * Directory with MediaPipe wasm files (vision_wasm_internal.wasm, ...).
   * null — the runtime bundled by Vite from the npm package (default).
   */
  wasmDir: string | null
  /** URL of the face_landmarker.task model. */
  modelUrl: string
}

const assetConfig: FaceLandmarkerAssetConfig = {
  wasmDir: null,
  modelUrl: bundledModelUrl,
}

/**
 * Override where the wasm runtime and model are fetched from. Call BEFORE the
 * first getFaceLandmarker()/preloadFaceLandmarker(). No-op for already-created
 * instances. Lets other projects host the assets at different paths.
 */
export function configureFaceLandmarker(config: Partial<FaceLandmarkerAssetConfig>): void {
  if (config.wasmDir !== undefined) assetConfig.wasmDir = config.wasmDir
  if (config.modelUrl !== undefined) assetConfig.modelUrl = config.modelUrl
}

// Exact SIMD probe MediaPipe itself runs (vision_bundle.mjs) to decide between
// the SIMD and no-SIMD wasm builds. We replicate it so our prefetch warms the
// SAME file MediaPipe will later load — otherwise we'd cache the wrong binary.
const MP_SIMD_PROBE = new Uint8Array([
  0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8,
  0, 65, 0, 253, 15, 253, 98, 11,
])

async function wasmSupportsSimd(): Promise<boolean> {
  try {
    await WebAssembly.instantiate(MP_SIMD_PROBE)
    return true
  } catch {
    return false
  }
}

// Fetch several URLs while reporting combined byte progress through onProgress.
async function fetchAllWithProgress(
  urls: string[],
  onProgress: (pct: number) => void,
): Promise<Uint8Array[]> {
  const responses = await Promise.all(urls.map((u) => fetch(u)))
  for (const res of responses) {
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`)
    if (!res.body) throw new Error('no stream body')
  }

  let total = 0
  for (const res of responses) {
    total += Number(res.headers.get('content-length')) || 0
  }
  if (total <= 0) throw new Error('unknown total size')

  let loaded = 0
  const readOne = async (res: Response): Promise<Uint8Array> => {
    const reader = res.body!.getReader()
    const chunks: Uint8Array[] = []
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      if (value) {
        chunks.push(value)
        loaded += value.byteLength
        onProgress(Math.min(99, Math.round((loaded / total) * 100)))
      }
    }
    const out = new Uint8Array(chunks.reduce((n, c) => n + c.byteLength, 0))
    let off = 0
    for (const c of chunks) {
      out.set(c, off)
      off += c.byteLength
    }
    return out
  }

  return Promise.all(responses.map(readOne))
}

async function createLandmarker(onProgress?: (pct: number) => void): Promise<FaceLandmarker> {
  // Тот же выбор SIMD / no-SIMD, что делает FilesetResolver: он умеет только
  // «каталог + фиксированные имена», а у файлов из `/assets/` имена с хэшем.
  const simd = await wasmSupportsSimd()
  const filesetResolver = assetConfig.wasmDir
    ? await FilesetResolver.forVisionTasks(assetConfig.wasmDir)
    : simd
      ? { wasmLoaderPath: simdLoaderUrl, wasmBinaryPath: simdBinaryUrl }
      : { wasmLoaderPath: noSimdLoaderUrl, wasmBinaryPath: noSimdBinaryUrl }

  const baseOpts = {
    baseOptions: { modelAssetPath: assetConfig.modelUrl },
    outputFaceBlendshapes: true,
    runningMode: 'VIDEO' as const,
    // Detect several faces so the composable can COUNT people in frame, pick the
    // most central as the primary subject, and reject frames with more than one
    // face in the oval (see detection.rejectMultipleFaces). Only faces actually
    // present are returned, so a single-face frame still yields one.
    numFaces: MAX_DETECTED_FACES,
  }

  // Progress-tracked fetch of the wasm binary (warms the browser cache for
  // MediaPipe's own load) + the model (passed directly as a buffer).
  let modelBuffer: Uint8Array | null = null
  try {
    const [, model] = await fetchAllWithProgress([filesetResolver.wasmBinaryPath, assetConfig.modelUrl], (pct) =>
      onProgress?.(pct),
    )
    modelBuffer = model ?? null
  } catch (e) {
    // Streaming/progress unsupported or network hiccup — fall back to the path loader.
    console.warn('MediaPipe progress prefetch failed, using path loader:', e)
    modelBuffer = null
  }

  // modelAssetBuffer may detach its backing ArrayBuffer once consumed, so the
  // CPU retry needs its own fresh copy of the bytes.
  const optionsFor = (delegate: 'GPU' | 'CPU') => {
    const baseOptions = modelBuffer
      ? { modelAssetBuffer: new Uint8Array(modelBuffer), delegate }
      : { ...baseOpts.baseOptions, delegate }
    return { ...baseOpts, baseOptions }
  }

  try {
    return await FaceLandmarker.createFromOptions(filesetResolver, optionsFor('GPU'))
  } catch {
    return await FaceLandmarker.createFromOptions(filesetResolver, optionsFor('CPU'))
  }
}

let instance: FaceLandmarker | null = null
let loading: Promise<FaceLandmarker> | null = null

/**
 * Get the shared FaceLandmarker, creating it on first call and reusing it after.
 * onProgress only fires for the call that actually starts the load (a background
 * preload starts it with no callback, so a later UI call just awaits silently).
 */
export function getFaceLandmarker(onProgress?: (pct: number) => void): Promise<FaceLandmarker> {
  if (instance) return Promise.resolve(instance)
  if (!loading) {
    loading = createLandmarker(onProgress)
      .then((l) => {
        instance = l
        return l
      })
      .catch((err) => {
        loading = null // allow a later retry on real use
        throw err
      })
  }
  return loading
}

/** Warm up MediaPipe in the background at app start (deferred to idle). */
export function preloadFaceLandmarker(): void {
  const run = () => {
    getFaceLandmarker().catch(() => {
      /* ignored — retried on real use */
    })
  }
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(run, { timeout: 3000 })
  } else {
    setTimeout(run, 1200)
  }
}
